import {EventProvider} from './event-provider';
import {Session} from './session';
import {SessionType} from './enum/session-type';
import {SessionFactoryEvent} from './enum/session-factory-event';
import {ConnectionEvent} from './enum/connection-event';
import {RedirectionsRequest} from './redirections-request';
import {ConnectionState} from './enum/connection-state';
import {ConnectionPropertiesRejectedError} from './connection-properties-rejected-error';
import {ConnectionPropertiesRejectedReason} from './enum/connection-properties-rejected-reason';
import {ConnectionProperties} from './connection-properties';
import {Bookmark} from './bookmark';
import {TrustedCollectionsService} from '../trusted-collections.service';
import {ConnectionFactory} from './connection-factory';
import {Logger} from './logger';
import {RdpConnection} from './rdp-connection';

export class SessionFactory {

    private readonly windowToSessionId: Record<number, string> = {};

    events = new EventProvider();

    constructor(
        private readonly connectionFactory: ConnectionFactory,
        private readonly logger: Logger,
        private readonly trustedCollections: TrustedCollectionsService
    ) {}

    private readonly onWindowCreated = (connection: RdpConnection, windowId: number): void => {
        const session = new Session(null, connection, SessionType.RemoteApp, this.logger);
        session.windowId = windowId;

        this.windowToSessionId[windowId] = session.id;
        this.events.fireEventCallback(SessionFactoryEvent.SessionCreated, session);
    };

    private readonly onWindowDeleted = (windowId: number): void => {
        const sessionId = this.windowToSessionId[windowId];
        if (!sessionId) { return; }

        delete this.windowToSessionId[windowId];
        this.events.fireEventCallback(SessionFactoryEvent.SessionDestroyed, sessionId);
    };

    private readonly onWindowActivated = (windowId: number): void => {
        const sessionId = this.windowToSessionId[windowId];
        if (!sessionId) { return; }

        this.events.fireEventCallback(SessionFactoryEvent.SessionFocused, sessionId);
    };

    private readonly onConnectionDestroyed = (connection: RdpConnection): void => {
        this.events.fireEventCallback(SessionFactoryEvent.ConnectionDestroyed, connection);

        connection.events.unsubscribe(ConnectionEvent.ConnectionDestroyed, this.onConnectionDestroyed);
        connection.events.unsubscribe(ConnectionEvent.WindowDeleted, this.onWindowDeleted);
        connection.events.unsubscribe(ConnectionEvent.WindowActivated, this.onWindowActivated);
        connection.events.unsubscribe(ConnectionEvent.WindowCreated, this.onWindowCreated);
    };

    private fireRedirectionsRequest(confirmation: any, connectionProperties: ConnectionProperties): void {
        const request = new RedirectionsRequest(confirmation, connectionProperties);
        this.events.fireEventCallback(SessionFactoryEvent.RedirectionsRequest, request);
    }

    completeConnectionCreation(
        connection: RdpConnection,
        connectionProperties: ConnectionProperties,
        rememberCollection: boolean,
        bookmark: Bookmark,
        hadSuccessfulConnection: boolean
    ): void {
        let createdSession: Session | null = null;

        try {
            if (!connection.isExistingConnection) {
                this.events.fireEventCallback(SessionFactoryEvent.ConnectionCreated, bookmark.id, connection);
            }

            if (this.trustedCollections && rememberCollection) {
                this.trustedCollections.trustCollection(connectionProperties);
            }

            connection.events.subscribe(ConnectionEvent.ConnectionDestroyed, this.onConnectionDestroyed);

            if (connectionProperties.remoteApplicationMode) {
                connection.events.subscribe(ConnectionEvent.WindowCreated, this.onWindowCreated);
                connection.events.subscribe(ConnectionEvent.WindowDeleted, this.onWindowDeleted);
                connection.events.subscribe(ConnectionEvent.WindowActivated, this.onWindowActivated);

                if (!hadSuccessfulConnection) {
                    try {
                        connection.launchApplication(
                            connectionProperties.remoteApplicationProgram,
                            connectionProperties.remoteApplicationCmdLine,
                            ''
                        );
                    } catch {
                        this.logger.debug?.('[SessionFactory] launchApplication failed');
                    }
                }

                if (connection.getState() !== ConnectionState.Connected) {
                    bookmark.desktopName = bookmark.workspaceName;
                    createdSession = new Session(bookmark, connection, SessionType.Desktop, this.logger);
                    this.events.fireEventCallback(SessionFactoryEvent.SessionCreated, createdSession);
                }
                return;
            }

            createdSession = new Session(bookmark, connection, SessionType.Desktop, this.logger);
            this.events.fireEventCallback(SessionFactoryEvent.SessionCreated, createdSession);
        } catch (err) {
            this.logger.error?.('[SessionFactory] completeConnectionCreation failed', err);
        }
    }

    createSession(bookmark: Bookmark, hadSuccessfulConnection: boolean, remoteAppId: number): void {
        if (!bookmark) { return; }
        if (!bookmark.rdpFileString) { return; }

        const connectionProperties = new ConnectionProperties(bookmark);
        const clipboardEnabled = connectionProperties.connectionSettings.EnableClipboard;
        const existingConnection = this.connectionFactory.findConnection(connectionProperties);

        bookmark.connectionProperties = connectionProperties;
        connectionProperties.connectionSettings.EnablePrinting = true;

        if (!connectionProperties.gatewayHostName) {
            connectionProperties.connectionSettings.UseGatewayProtocol = false;
            connectionProperties.connectionSettings.UseSecureWebSockets = true;
            connectionProperties.connectionSettings.GatewayHostName = connectionProperties.hostName;
            connectionProperties.connectionSettings.GatewayPort = 3392;
            connectionProperties.connectionSettings.GatewayPath = 'rdp';
        }

        const canProceedWithoutConfirmation =
            !clipboardEnabled ||
            (existingConnection && existingConnection.getState() !== ConnectionState.Disconnected) ||
            (this.trustedCollections && this.trustedCollections.isCollectionTrusted(connectionProperties));

        const complete = (props: ConnectionProperties, rememberCollection: boolean): void => {
            try {
                this.connectionFactory.createConnection(
                    props,
                    (rdpConnection: RdpConnection): void => {
                        this.completeConnectionCreation(
                            rdpConnection,
                            props,
                            rememberCollection,
                            bookmark,
                            hadSuccessfulConnection
                        );
                    },
                    remoteAppId
                );
            } catch (err) {
                if (props.connectionSettings.UseGatewayProtocol) {
                    this.events.fireEventCallback(SessionFactoryEvent.ConnectionCreateFailed, err);
                    return;
                }

                this.events.fireEventCallback(
                    SessionFactoryEvent.ConnectionCreateFailed,
                    new ConnectionPropertiesRejectedError(ConnectionPropertiesRejectedReason.GatewayNotSpecified, bookmark)
                );
            }
        };

        if (canProceedWithoutConfirmation) {
            complete(connectionProperties, false);
            return;
        }

        const confirmation = {
            complete,
            cancel: (): void => {
                this.logger.error('[SessionFactory] Redirections request cancelled by user');
            }
        };

        this.fireRedirectionsRequest(confirmation, connectionProperties);
    }
}
